import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Menu from '../menu/Menu';
import RTTable from '../common/Table';
import RTSpinner from '../common/RTSpinner';
import RTButton from '../common/RTButton';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { apiGet, apiPost } from '../../api/apiClient';
import { toastSuccess, toastError, toastConfirm } from '../common/toast';
import tenantStorage from '../../utils/tenantStorage';
import '../common/rtCommon.css';
import './WhatsAppLogReport.css';

const WhatsAppLogReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [isResendingFailed, setIsResendingFailed] = useState(false);
  
  // Get first day of current month and today's date
  const getDefaultDates = () => {
    const today = new Date();
    const firstDayCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      from_date: firstDayCurrentMonth,
      to_date: today
    };
  };
  
  const [filters, setFilters] = useState({
    from_date: getDefaultDates().from_date,
    to_date: getDefaultDates().to_date,
    template_name: '',
    status: ''
  });
  const [appliedFilters, setAppliedFilters] = useState({
    from_date: getDefaultDates().from_date,
    to_date: getDefaultDates().to_date,
    template_name: '',
    status: ''
  });

  const fetchLogs = async (filterParams = appliedFilters, abortController = null) => {
    setLoading(true);
    try {
      const params = {};
      if (filterParams.status) params.status = filterParams.status;
      if (filterParams.template_name) params.template_name = filterParams.template_name;
      
      // Format dates to YYYY-MM-DD in local timezone
      if (filterParams.from_date) {
        const year = filterParams.from_date.getFullYear();
        const month = String(filterParams.from_date.getMonth() + 1).padStart(2, '0');
        const day = String(filterParams.from_date.getDate()).padStart(2, '0');
        params.from_date = `${year}-${month}-${day}`;
      }
      if (filterParams.to_date) {
        const year = filterParams.to_date.getFullYear();
        const month = String(filterParams.to_date.getMonth() + 1).padStart(2, '0');
        const day = String(filterParams.to_date.getDate()).padStart(2, '0');
        params.to_date = `${year}-${month}-${day}`;
      }
      
      console.log('Applied filters:', filterParams);
      console.log('API params:', params);
      
      const token = tenantStorage.getItem('token');
      const response = await apiGet('/api/whatsapp/logs', { 
        params: params,
        headers: { 'Authorization': `Bearer ${token}` },
        signal: abortController?.signal
      });
      
      const apiResponse = response.data;
      if (apiResponse && Array.isArray(apiResponse)) {
        setLogs(apiResponse);
      } else {
        setLogs([]);
        toastError('No WhatsApp logs found for the selected filters.');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error loading WhatsApp logs:', err);
        toastError('Failed to fetch WhatsApp logs');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = tenantStorage.getItem('token');
      const response = await apiGet('/api/whatsapp/templates', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data && response.data.templates) {
        setTemplates(response.data.templates);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error loading template names:', err);
      }
    }
  };

  useEffect(() => {
    fetchTemplates();
    fetchLogs(appliedFilters);
    
    // eslint-disable-next-line
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    setAppliedFilters(filters);
    fetchLogs(filters);
  };

  const resetFilters = () => {
    const defaultFilters = {
      from_date: getDefaultDates().from_date,
      to_date: getDefaultDates().to_date,
      template_name: '',
      status: ''
    };
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    fetchLogs(defaultFilters);
  };

  const handleResend = async (log) => {
    try {
      setLoading(true);
      const token = tenantStorage.getItem('token');
      
      const response = await apiPost(`/api/whatsapp/resend/${log.id}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const apiResponse = response.data;
      if (apiResponse && apiResponse.success) {
        toastSuccess('Message resent successfully!');
        // Reload the page to refresh data
        window.location.reload();
      } else {
        toastError(apiResponse?.error || 'Failed to resend message');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error resending message:', err);
      toastError('Failed to resend message');
      setLoading(false);
    }
  };

  const handleToggleDoNotSend = async (log) => {
    try {
      const token = tenantStorage.getItem('token');
      
      const response = await apiPost(`/api/whatsapp/logs/${log.id}/mark-do-not-send`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const apiResponse = response.data;
      if (apiResponse && apiResponse.success) {
        const action = apiResponse.do_not_send ? 'enabled' : 'disabled';
        toastSuccess(`Do not send flag ${action} successfully`);
        // Refresh logs to show updated status
        fetchLogs(appliedFilters);
      } else {
        toastError('Failed to toggle do not send flag');
      }
    } catch (err) {
      console.error('Error toggling do not send flag:', err);
      toastError('Failed to toggle do not send flag');
    }
  };

  const handleResendFailed = async () => {
    const confirmed = await toastConfirm(
      true,
      'Are you sure you want to resend all failed WhatsApp messages (excluding blocked numbers)?'
    );
    if (!confirmed) {
      return;
    }

    setIsResendingFailed(true);
    try {
      const token = tenantStorage.getItem('token');
      
      const response = await apiPost('/api/whatsapp/resend-failed', {}, {
        headers: { 'Authorization': `Bearer ${token}` },
        timeout: 300000 // 5 minutes timeout for bulk resend
      });
      
      const apiResponse = response.data;
      if (apiResponse && apiResponse.success) {
        const { succeeded, failed, total } = apiResponse;
        toastSuccess(
          `Resend completed: ${succeeded} succeeded, ${failed} failed out of ${total} attempts`
        );
        fetchLogs(appliedFilters);
      } else {
        toastError('Failed to resend messages');
      }
    } catch (err) {
      console.error('Error resending failed messages:', err);
      toastError(err.response?.data?.detail || 'Failed to resend messages');
    } finally {
      setIsResendingFailed(false);
    }
  };

  const handleRefresh = () => {
    toastSuccess('Refreshing logs...');
    fetchLogs(appliedFilters);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getStatusBadgeClass = (status) => {
    const baseClass = 'whatsapp-log-status-badge';
    
    if (status === 'success') {
      return `${baseClass} whatsapp-log-status-success`;
    } else if (status === 'failed') {
      return `${baseClass} whatsapp-log-status-failed`;
    } else {
      return `${baseClass} whatsapp-log-status-unknown`;
    }
  };

  const getDeliveryStatusBadgeClass = (deliveryStatus) => {
    const baseClass = 'whatsapp-log-delivery-badge';
    
    switch (deliveryStatus) {
      case 'sent':
        return `${baseClass} whatsapp-log-delivery-sent`;
      case 'delivered':
        return `${baseClass} whatsapp-log-delivery-delivered`;
      case 'read':
        return `${baseClass} whatsapp-log-delivery-read`;
      case 'failed':
        return `${baseClass} whatsapp-log-delivery-failed`;
      default:
        return `${baseClass} whatsapp-log-delivery-pending`;
    }
  };

  const columns = [
    { 
      Header: 'Sent At', 
      accessor: 'created_at',
      Cell: ({ value }) => formatTimestamp(value)
    },
    { Header: 'Phone Number', accessor: 'phone_number' },
    { Header: 'Template', accessor: 'template_name' },
    { 
      Header: 'Parameters', 
      accessor: 'params',
      Cell: ({ value }) => {
        if (!value) return '-';
        try {
          const params = typeof value === 'string' ? JSON.parse(value) : value;
          return <span className="whatsapp-log-params-text">{JSON.stringify(params)}</span>;
        } catch (e) {
          return value;
        }
      }
    },
    { 
      Header: 'Status', 
      accessor: 'status',
      Cell: ({ value }) => (
        <span className={getStatusBadgeClass(value)}>
          {value || 'Unknown'}
        </span>
      )
    },
    { 
      Header: 'Delivery Status', 
      accessor: 'delivery_status',
      Cell: ({ value }) => (
        <span className={getDeliveryStatusBadgeClass(value)}>
          {value || 'Pending'}
        </span>
      )
    },
    { 
      Header: 'Do Not Send', 
      accessor: 'do_not_send',
      Cell: ({ value }) => value ? 'Yes' : ''
    },
    { 
      Header: 'Error Message', 
      accessor: 'error_message',
      Cell: ({ value }) => value || '-'
    },
    {
      Header: 'Actions',
      accessor: 'id',
      Cell: ({ row }) => {
        const log = row;
        
        if (!log) return '-';
        
        const isFailed = log.status === 'failed' || log.delivery_status === 'failed';
        const showResend = isFailed && log.template_name === 'payment_link' && !log.do_not_send;
        const showDoNotSend = isFailed;
        
        if (!isFailed) return '-';
        
        return (
          <div className="d-flex gap-2">
            {showResend && (
              <RTButton
                type="link"
                size="sm"
                onClick={() => handleResend(log)}
                disabled={loading}
              >
                Resend
              </RTButton>
            )}
            {showDoNotSend && (
              <RTButton
                type="link"
                size="sm"
                onClick={() => handleToggleDoNotSend(log)}
                disabled={loading}
              >
                {log.do_not_send ? 'Unblock' : 'Block'}
              </RTButton>
            )}
          </div>
        );
      }
    }
  ];

  return (
    <div className="rt-layout">
      <Menu className="rt-menu" activePath={location.pathname} />
      <div className="rt-main-content">
        <div className="rt-title-row">
          <h2 className="rt-title">WhatsApp Message Logs</h2>
          <div className="d-flex gap-2">
            <RTButton
              variant="secondary"
              size="sm"
              onClick={() => navigate('/reports')}
            >
              &#8592; Back
            </RTButton>
            <RTButton
              variant="warning"
              size="sm"
              onClick={handleResendFailed}
              disabled={loading || isResendingFailed}
            >
              {isResendingFailed ? '⏳ Resending...' : 'Resend Failed'}
            </RTButton>
            <RTButton
              variant="primary"
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
            >
              Refresh
            </RTButton>
          </div>
        </div>

        {/* Filters Section */}
        <div className="rt-section" style={{ flex: 'none' }}>
          <div className="row mb-3">
            <div className="col-md-2">
              <label className="form-label whatsapp-log-filter-label">From Date</label>
              <DatePicker
                selected={filters.from_date}
                onChange={(date) => handleFilterChange('from_date', date)}
                dateFormat="dd/MM/yyyy"
                className="form-control"
                placeholderText="DD/MM/YYYY"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                autoComplete="off"
              />
            </div>
            <div className="col-md-2">
              <label className="form-label whatsapp-log-filter-label">To Date</label>
              <DatePicker
                selected={filters.to_date}
                onChange={(date) => handleFilterChange('to_date', date)}
                dateFormat="dd/MM/yyyy"
                className="form-control"
                placeholderText="DD/MM/YYYY"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                autoComplete="off"
              />
            </div>
            <div className="col-md-3">
              <label className="form-label whatsapp-log-filter-label">Template Name</label>
              <select
                className="form-select"
                value={filters.template_name || ''}
                onChange={(e) => handleFilterChange('template_name', e.target.value)}
              >
                <option value="">All Templates</option>
                {templates.map(template => (
                  <option key={template} value={template}>{template}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label whatsapp-log-filter-label">Status</label>
              <select
                className="form-select"
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Status</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div className="col-md-3 d-flex align-items-end">
              <RTButton
                variant="primary"
                onClick={applyFilters}
                className="me-2"
                disabled={loading}
              >
                Apply Filters
              </RTButton>
              <RTButton
                variant="outline-secondary"
                onClick={resetFilters}
                disabled={loading}
              >
                Reset
              </RTButton>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="rt-section">
          {loading ? (
            <div className="whatsapp-log-spinner-container">
              <RTSpinner message='Loading WhatsApp logs...' />
            </div>
          ) : (
            <RTTable
              columns={columns}
              data={logs}
              emptyMessage="No WhatsApp logs found for the selected criteria"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatsAppLogReport;
